import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { BulkBatchDto, Paged, UnitDto, UnitKind } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import type { AgentChain } from '../../components/AgentChainSelect';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useBulkSubmit } from './api';
import { statusLabel } from './labels';

/** Adjustments (PC47): the two single re-assign pages, as in the reference. */
export function AdjustmentsPage() {
  const { can } = useAuth();
  const links = [
    { label: 'Single Inventory Re-assign', code: 'PC38', to: '/inventory/adjustments/sim' },
    { label: 'Single Device Re-assign', code: 'PC32', to: '/inventory/adjustments/device' },
  ].filter((l) => can(l.code));
  return (
    <Box>
      <PageHeader title="Adjustments" code="PC47" />
      <Box
        component="nav"
        aria-label="Adjustments"
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}
      >
        {links.map((l) => (
          <Card key={l.to} variant="outlined">
            <CardActionArea component={Link} to={l.to}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{l.label}</Typography>
                <Chip size="small" label={l.code} />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

type SearchBy = 'identifier' | 'box';

const PAGE: Record<
  UnitKind,
  { title: string; code: string; noun: string; operation: 'adjust-sim' | 'adjust-device' }
> = {
  sim: { title: 'Inventory SIM Re-assign', code: 'PC38', noun: 'SIM', operation: 'adjust-sim' },
  device: {
    title: 'Inventory Device Re-assign',
    code: 'PC32',
    noun: 'Device ID',
    operation: 'adjust-device',
  },
};

/**
 * "Single Inventory Re-assign" (PC38) and "Single Device Re-assign" (PC32): find stock by its
 * number or its box number, then move what was found to an agent. The reference also searches
 * by Box ID, the boxes of "Ship SIM to Agent (Store) By Creating Boxes" (PC477), which are not
 * built here. Its re-assign form was not seen; this one asks for the agent chain.
 */
export function UnitReassignPage({ kind }: { kind: UnitKind }) {
  const page = PAGE[kind];
  const notify = useNotify();
  const [by, setBy] = useState<SearchBy>('identifier');
  const [text, setText] = useState('');
  const [query, setQuery] = useState<Record<string, string> | null>(null);
  const [problem, setProblem] = useState('');
  const [chain, setChain] = useState<AgentChain>({});
  const [result, setResult] = useState<BulkBatchDto | null>(null);
  // Nothing is loaded until the first search.
  const units = useQuery({
    queryKey: ['inventory', 'units', 'adjust', kind, query],
    queryFn: () =>
      api.get<Paged<UnitDto>>('/inventory/units', { kind, pageSize: 200, ...(query ?? {}) }),
    enabled: Boolean(query),
  });
  const reassign = useBulkSubmit(page.operation);
  const found = query ? (units.data?.data ?? []) : [];

  const search = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) {
      setProblem(`Enter the ${by === 'box' ? 'Box Number' : page.noun}`);
      return;
    }
    setProblem('');
    setResult(null);
    setQuery({ [by]: value });
  };

  const submit = async () => {
    const owner = deepestAgent(chain);
    if (!owner) {
      setProblem('Select the agent to assign to');
      return;
    }
    try {
      const batch = await reassign.mutateAsync({
        type: 'paste',
        identifiers: found.map((u) => (kind === 'sim' ? u.sim : u.imei) ?? ''),
        params: { ownerUserId: owner },
      });
      setResult(batch);
      void units.refetch();
    } catch (err) {
      notify.error(err);
    }
  };

  const columns: Column<UnitDto>[] = [
    {
      key: 'id',
      header: kind === 'sim' ? 'SIM' : 'Device ID',
      render: (u) => (kind === 'sim' ? u.sim : u.imei) ?? '',
    },
    { key: 'carrier', header: 'Carrier', render: (u) => u.carrier.code },
    { key: 'status', header: 'Status', render: (u) => statusLabel(u.status) },
    { key: 'store', header: 'Store ID', render: (u) => u.owner?.loginId ?? '' },
    { key: 'box', header: 'Box', render: (u) => u.details.box },
  ];

  return (
    <Box>
      <PageHeader title={page.title} code={page.code} />
      <Paper variant="outlined" component="form" noValidate onSubmit={search} sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ mb: 1 }}>
          Enter {page.noun === 'SIM' ? 'SIM Number' : page.noun} OR Box Number:
        </Typography>
        <RadioGroup
          row
          value={by}
          onChange={(e) => setBy(e.target.value as SearchBy)}
          aria-label="Search by"
        >
          <FormControlLabel
            value="identifier"
            control={<Radio />}
            label={`Search by ${page.noun}`}
          />
          <FormControlLabel value="box" control={<Radio />} label="Search by Box Number" />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1 }}>
          <TextField
            label={by === 'box' ? 'Box Number' : page.noun}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setProblem('');
            }}
            error={Boolean(problem) && !query}
            helperText={!query ? problem || undefined : undefined}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
        </Box>
      </Paper>

      {query ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <DataTable
            label="Search result"
            columns={columns}
            rows={found}
            rowKey={(u) => u.id}
            loading={units.isFetching}
            error={units.error}
            onRetry={() => void units.refetch()}
            emptyText="No record found"
          />
          {found.length ? (
            <Box
              sx={{
                mt: 2,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
              }}
            >
              <AgentChainSelect
                value={chain}
                onChange={(next) => {
                  setChain(next);
                  setProblem('');
                }}
              />
              {problem ? (
                <Typography color="error" sx={{ gridColumn: '1 / -1' }}>
                  {problem}
                </Typography>
              ) : null}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Button
                  variant="contained"
                  onClick={() => void submit()}
                  disabled={reassign.isPending}
                >
                  Re-assign
                </Button>
              </Box>
            </Box>
          ) : null}
          {result ? (
            <Alert severity={result.failure ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {result.name}: {result.total} record(s), {result.success} success, {result.failure}{' '}
              failure. The rows are in the{' '}
              {kind === 'sim' ? 'Re-assign SIM Report (PC310)' : 'Re-assign Device Report (PC311)'}.
            </Alert>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
}
